import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Cable,
  FolderTree,
  Gauge,
  KeyRound,
  Package,
  Presentation,
  Settings,
  type LucideIcon,
} from "lucide-react";
import { AdminIcon, RootAdminIcon, SalesIcon } from "../modules/core/index.js";
import { onDemoSessionEnded } from "../modules/accounts/index.js";
import { useQueryClient } from "@tanstack/react-query";
import { createAdminApi, type AdminApi } from "./adminApi.js";
import { type AdminCredentials } from "../modules/core/index.js";
import { ADMIN_API_BASE_URL } from "../modules/core/index.js";
import { AcceptInvitation, AdminLogin, ForgotPassword, ResetPassword } from "../modules/accounts/index.js";
import { CLIENTS_PATH } from "../modules/clients/index.js";
import type { AdminMenuGroup, AdminPageContribution, AdminSession } from "../modules/core/index.js";
import { CountBadge } from "../modules/core/index.js";
import { adminFeedbackApi } from "../modules/feedback/index.js";
import { FeedbackButton } from "../modules/feedback/index.js";
import { Link, getOriginPath, navigate } from "../modules/core/index.js";
import {
  ShownScreenProvider,
  toAdminPath,
  useAdminTrail,
} from "../modules/core/index.js";
import { clearApiCalls } from "../modules/core/index.js";
import { ApiCallInspector } from '../modules/core/index.js'
import { EndpointHintsProvider } from '../modules/core/index.js'
import { Button, SignOutButton } from '../modules/core/index.js'
import { HelpSeatProvider } from '../modules/core/index.js'
import { FEATURES, MODULES } from '../webModules.js'

type AdminRoute = AdminPageContribution<AdminApi>;

/** Every page the modules declare (src/webModules.ts), in module order, then the add-ons' (which build their calls on `api.client`); a page names its group and whether it is the root's or a sales login's. */
const ALL_PAGES: readonly AdminRoute[] = [
  ...MODULES.flatMap((module) => module.adminPages ?? []),
  ...FEATURES.flatMap((feature) => feature.adminPages ?? []),
];

interface NavGroup {
  key: AdminMenuGroup;
  heading: string;
  /** Shown before the heading on the group's tab; not one a page of the group already wears. */
  icon: LucideIcon;
  routes: readonly AdminRoute[];
}

/**
 * The menu's groups, in the order they are shown. A group is a tab in the
 * header's first row, and its pages fill the second row while one of them
 * is open: who can sign in, what exists under a client, how workers are
 * measured, tools for trying the API out, the product itself, and the
 * root's own view of the system. A page joins a group by naming it in its
 * module's declaration (modules/<name>/webModule.tsx), in module order; a
 * group with no page the signed-in admin may see has no tab. Demos is a
 * sales login's whole area: the API gives a sales login nothing else, so
 * that menu is what would work, not a way of hiding the rest.
 */
const MENU_GROUPS: readonly Omit<NavGroup, "routes">[] = [
  { key: "accounts", heading: "Accounts", icon: KeyRound },
  { key: "directory", heading: "Directory", icon: FolderTree },
  { key: "scoring", heading: "Scoring", icon: Gauge },
  { key: "integration", heading: "Integration", icon: Cable },
  { key: "product", heading: "Product", icon: Package },
  { key: "system", heading: "System", icon: Settings },
  { key: "demos", heading: "Demos", icon: Presentation },
];

/** The pages this sign-in's area is made of: a sales login's own, or an admin's. The two never mix. */
function pagesFor(session: AdminSession): readonly AdminRoute[] {
  const isSales = session.role === "sales";
  return ALL_PAGES.filter((page) => (page.sales ?? false) === isSales);
}

/** The menu for this sign-in: each group with the pages in it that are reached from the menu rather than from another page, and that this admin may see; the root's own pages last in their group. */
function navGroupsFor(session: AdminSession): readonly NavGroup[] {
  const pages = pagesFor(session);
  const isRoot = session.role === "root";
  return MENU_GROUPS.map((group) => ({
    ...group,
    routes: pages
      .filter(
        (page) =>
          page.group === group.key && !page.reachedFrom && (!page.rootOnly || isRoot),
      )
      .sort((a, b) => Number(a.rootOnly ?? false) - Number(b.rootOnly ?? false)),
  })).filter((group) => group.routes.length > 0);
}

/** An unrecognised /admin/... path falls back to the first page (Clients), as it always has; so does a root-only path for anyone but the root, and any admin page for a sales login. */
function routeFor(path: string, session: AdminSession): AdminRoute {
  const pages = pagesFor(session);
  const route = pages.find((candidate) => candidate.path === path);
  return route && (!route.rootOnly || session.role === "root")
    ? route
    : pages[0]!;
}

/**
 * Returns to the screen the admin came from, exactly as they left it. Steps
 * back through the browser's history when that is where the screen is, so
 * the browser's own buttons stay in step with this one.
 */
function goBackTo(path: string): void {
  const origin = getOriginPath();
  if (origin !== null && toAdminPath(origin) === path) {
    window.history.back();
  } else {
    navigate(path);
  }
}

function NavItem({
  route,
  isActive,
  badge,
}: {
  route: AdminRoute;
  isActive: boolean;
  badge: number;
}) {
  const Icon = route.icon;
  return (
    <Link
      to={route.path}
      aria-current={isActive ? "page" : undefined}
      className={`group relative flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-[13px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral ${
        isActive
          ? "bg-white/10 text-white shadow-[inset_0_-2px_0_0_#F26B5B]"
          : "text-white/65 hover:bg-white/5 hover:text-white"
      }`}
    >
      <Icon
        aria-hidden="true"
        strokeWidth={isActive ? 2.25 : 1.75}
        className={`h-4 w-4 shrink-0 ${isActive ? "text-coral" : "text-white/50 group-hover:text-white/80"}`}
      />
      {route.label}
      <CountBadge count={badge} />
    </Link>
  );
}

function AdminShell({
  api,
  session,
  onFeedbackSeen,
  onPasswordRotated,
  onNameChanged,
  onSignOut,
}: {
  api: AdminApi;
  session: AdminSession;
  onFeedbackSeen: () => void;
  onPasswordRotated: (username: string, password: string) => void;
  onNameChanged: (admin: { username: string; firstName: string; lastName: string }) => void;
  onSignOut: () => void;
}) {
  const { path, kept } = useAdminTrail();
  const isSales = session.role === "sales";
  const navGroups = navGroupsFor(session);
  const current = routeFor(path, session);
  const isRoot = session.role === "root";
  const displayName =
    `${session.firstName} ${session.lastName}`.trim() || session.username;

  // A demo session this admin opened has ended and its tab is closing onto this one: be on the default page when it does.
  useEffect(() => onDemoSessionEnded(true, () => navigate(CLIENTS_PATH)), []);

  // Whatever a page does when it comes on screen: the Feedback page counts its new items as seen.
  useEffect(
    () => current.onShown?.({ session, onFeedbackSeen }),
    [current, session, onFeedbackSeen],
  );
  // A page reached from another one (the onboarding form, from Clients) keeps that page's menu item lit.
  const activeMenuPath = current.reachedFrom?.path ?? current.path;
  const activeGroup =
    navGroups.find((group) =>
      group.routes.some((route) => route.path === activeMenuPath),
    ) ?? navGroups[0]!;
  const cameFrom = kept.at(-1);

  return (
    // The paths beside section headings are a developer's view, like the inspector below: for SeeWhoCares admins, not a sales login.
    <EndpointHintsProvider value={!isSales}>
      <div className="min-h-screen bg-canvas">
        <div className="bg-marine-dk shadow-md">
          <div className="flex items-center justify-between gap-4 px-6 pt-3">
            <div className="flex items-baseline gap-2.5">
              <span className="text-[9px] font-bold uppercase tracking-[2.5px] text-coral">
                SeeWhoCares
              </span>
              <span className="text-sm font-black text-white">
                {isSales ? "Demos" : "Platform Admin"}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden text-xs text-white/60 sm:inline">
                Signed in as{" "}
                <span className="font-semibold text-white">{displayName}</span>
                <span
                  className={`ml-1.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${isRoot ? "bg-coral text-white" : "bg-white/15 text-white/80"}`}
                >
                  {isRoot ? (
                    <RootAdminIcon className="h-3 w-3" />
                  ) : isSales ? (
                    <SalesIcon className="h-3 w-3" />
                  ) : (
                    <AdminIcon className="h-3 w-3" />
                  )}
                  {isRoot ? "Root" : isSales ? "Sales" : "Admin"}
                </span>
              </span>
              <SignOutButton onSignOut={onSignOut} />
            </div>
          </div>

          {/* Two tiers: the groups as tabs, then the pages of the group the current page is in. A new page lengthens only its group's row, and a new group adds one tab. */}
          <nav aria-label="Admin" className="px-4 pb-2">
            {/* A row that scrolls sideways clips whatever sticks out of it, so the headroom and the right-hand room are for the count badge that sits on a tab's corner. */}
            <div className="flex items-end gap-1 overflow-x-auto pt-1.5 pr-1.5">
              {navGroups.map((group) => {
                const first = group.routes[0]!;
                const isActive = group === activeGroup;
                const badge = group.routes.reduce(
                  (total, route) => total + (route.badge?.(session) ?? 0),
                  0,
                );
                return (
                  <Link
                    key={group.heading}
                    to={first.path}
                    aria-current={isActive ? "true" : undefined}
                    className={`relative flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-[10px] font-bold uppercase tracking-[2px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral ${
                      isActive
                        ? "text-white shadow-[inset_0_-2px_0_0_#F26B5B]"
                        : "text-white/45 hover:text-white/80"
                    }`}
                  >
                    <group.icon aria-hidden="true" className="h-3.5 w-3.5" />
                    {group.heading}
                    <CountBadge count={badge} />
                  </Link>
                );
              })}
            </div>
            <div className="mt-1.5 flex items-center gap-0.5 overflow-x-auto border-t border-white/10 pt-1.5 pr-1.5">
              {activeGroup.routes.map((route) => (
                <NavItem
                  key={route.path}
                  route={route}
                  isActive={route.path === activeMenuPath}
                  badge={route.badge?.(session) ?? 0}
                />
              ))}
            </div>
          </nav>
        </div>

        <main>
          <header className="border-b border-marine/15 bg-white px-7 py-5">
            {cameFrom && (
              <Button icon={ArrowLeft} variant="secondary" size="sm" onClick={() => goBackTo(cameFrom.path)} className="mb-2">
                Back to {routeFor(cameFrom.path, session).title}
              </Button>
            )}
            {current.reachedFrom && !cameFrom && (
              <Link
                to={current.reachedFrom.path}
                className="mb-1 inline-flex items-center gap-1 text-[11px] font-bold text-marine hover:text-marine-dk"
              >
                <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
                {current.reachedFrom.label}
              </Link>
            )}
            <h1 className="flex items-center gap-2.5 text-xl font-black text-ink">
              <current.icon aria-hidden="true" className="h-5 w-5 text-marine" />
              {current.title}
            </h1>
          </header>
          {/* The screens that led here stay mounted but hidden, so Back finds them as they were left - open dialogs, expanded rows, typed searches and all. */}
          {[...kept.map((place) => place.path), path].map((screenPath) => {
            const Screen = routeFor(screenPath, session).page;
            return (
              <div key={screenPath} hidden={screenPath !== path}>
                <ShownScreenProvider value={screenPath === path}>
                  <Screen
                    api={api}
                    session={session}
                    onPasswordRotated={onPasswordRotated}
                    onNameChanged={onNameChanged}
                    onFeedbackSeen={onFeedbackSeen}
                  />
                </ShownScreenProvider>
              </div>
            );
          })}
        </main>

        {/* SeeWhoCares staff only: the inspector shows every call this page made, which is a developer's view, not an operator's - nor a sales login's. */}
        {!isSales && <ApiCallInspector />}
        <FeedbackButton
          api={adminFeedbackApi(api)}
          aboveInspector
          badge={session.unseenFeedback}
          details={{
            role: isRoot ? "SeeWhoCares root admin" : isSales ? "SeeWhoCares sales" : "SeeWhoCares admin",
            admin: session.username,
            view: current.title,
            cameFrom: cameFrom
              ? routeFor(cameFrom.path, session).title
              : undefined,
          }}
        />
      </div>
    </EndpointHintsProvider>
  );
}

/**
 * SeeWhoCares-only area at /admin: view every client and onboard a new one.
 * Signs in with a platform-admin credential - the configured root, or an
 * admin login the root created - which is sent as HTTP Basic Auth on every
 * admin request. That credential can act on every client, so it is held in
 * React state only - never in sessionStorage like the operator token - and
 * a reload asks for it again.
 */
export default function AdminApp() {
  const queryClient = useQueryClient();
  const [credentials, setCredentials] = useState<AdminCredentials | null>(null);
  const [session, setSession] = useState<AdminSession | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const api = useMemo(
    () =>
      credentials && ADMIN_API_BASE_URL
        ? createAdminApi(ADMIN_API_BASE_URL, credentials)
        : null,
    [credentials],
  );

  async function signIn(candidate: AdminCredentials): Promise<void> {
    if (!ADMIN_API_BASE_URL) {
      setError("VITE_API_BASE_URL is not configured");
      return;
    }
    setIsVerifying(true);
    setError(null);
    try {
      const login = await createAdminApi(ADMIN_API_BASE_URL, candidate).login();
      setSession({ ...login, unseenFeedback: login.newFeedback });
      setCredentials(candidate);
    } catch (err) {
      setError(
        err instanceof Error && err.name === "ApiError"
          ? err.message
          : "Sign in is temporarily unavailable",
      );
    } finally {
      setIsVerifying(false);
    }
  }

  function signOut(): void {
    queryClient.removeQueries({ queryKey: ["admin"] });
    clearApiCalls();
    setCredentials(null);
    setSession(null);
  }

  const markFeedbackSeen = useCallback(
    () =>
      setSession((current) =>
        current ? { ...current, unseenFeedback: 0 } : current,
      ),
    [],
  );
  // The header shows the session's name, so a rename of the signed-in admin shows at once rather than at the next sign-in.
  const followOwnName = useCallback(
    (admin: { username: string; firstName: string; lastName: string }) =>
      setSession((current) =>
        current && admin.username === current.username
          ? { ...current, firstName: admin.firstName, lastName: admin.lastName }
          : current,
      ),
    [],
  );
  // The old password stops working the moment it is rotated, so the session must carry on with the new one or every next request would fail.
  const switchOwnPassword = useCallback(
    (username: string, password: string) =>
      setCredentials((current) =>
        current && username === session?.username
          ? { ...current, password }
          : current,
      ),
    [session?.username],
  );

  if (!api || !credentials || !session) {
    // The self-service reset (invitations plan phase 4): the admin's link opens here, and signs in again afterwards.
    if (window.location.pathname === "/admin/forgot-password") {
      return <ForgotPassword signInPath="/admin" />;
    }
    if (window.location.pathname === "/admin/reset-password") {
      return <ResetPassword signInPath="/admin" />;
    }
    // An invited admin sets their password here and is signed in with it (invitations plan D11).
    if (window.location.pathname === "/admin/accept-invitation") {
      return (
        <AcceptInvitation
          onAdminAccepted={(candidate) => {
            window.history.replaceState(null, "", "/admin");
            void signIn(candidate);
          }}
        />
      );
    }
    return (
      <AdminLogin
        isVerifying={isVerifying}
        error={error}
        onSubmit={(candidate) => void signIn(candidate)}
      />
    );
  }

  return (
    <HelpSeatProvider seat={session.role === "sales" ? "sales" : "admin"}>
      <AdminShell
        api={api}
        session={session}
        onFeedbackSeen={markFeedbackSeen}
        onPasswordRotated={switchOwnPassword}
        onNameChanged={followOwnName}
        onSignOut={signOut}
      />
    </HelpSeatProvider>
  );
}
